import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import api from '../../services/api';
import { ROLE_CHOICES, FUNDING_TYPE_CHOICES, COUNTRY_CHOICES, COUNTIES_BY_COUNTRY } from '../../utils/constants';

const ProfileSignUpForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();

  // Get redirect intent passed from verification
  const redirectIntent = location.state?.redirectIntent;

  const [profile, setProfile] = useState({
    roles: [],
    role_other_text: '',
    country: [],
    counties: [],
    funding_types_of_interest: [],
    funding_types_other_text: '',
    email_notifications: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEditingLocation, setIsEditingLocation] = useState(true);

  useEffect(() => {
    posthog.capture('profile_completion_started');
  }, [posthog]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setProfile(prev => {
      const newValues = prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value];

      // If unchecking 'other', clear the other text field
      if (field === 'roles' && value === 'other' && !newValues.includes('other')) {
        return {
          ...prev,
          [field]: newValues,
          role_other_text: ''
        };
      }
      if (field === 'funding_types_of_interest' && value === 'other' && !newValues.includes('other')) {
        return {
          ...prev,
          [field]: newValues,
          funding_types_other_text: ''
        };
      }

      return {
        ...prev,
        [field]: newValues
      };
    });
  };

  const handleCountryToggle = (countryValue) => {
    setProfile(prev => {
      const isSelected = prev.country.includes(countryValue);
      const newCountry = isSelected
        ? prev.country.filter(c => c !== countryValue)
        : [...prev.country, countryValue];

      if (isSelected) {
        const countryEntry = COUNTIES_BY_COUNTRY.find(c => c.country === countryValue);
        const countiesToRemove = countryEntry ? countryEntry.counties : [];
        return {
          ...prev,
          country: newCountry,
          counties: prev.counties.filter(c => !countiesToRemove.includes(c)),
        };
      }

      return { ...prev, country: newCountry };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Validation: at least one role
    if (profile.roles.length === 0) {
      setError('Please select at least one role');
      setSaving(false);
      return;
    }

    // Validation: if "other" is selected for roles, text must be provided
    if (profile.roles.includes('other') && !profile.role_other_text.trim()) {
      setError('Please specify your role in the "Other" field');
      setSaving(false);
      return;
    }

    // Validation: if "other" is selected for funding types, text must be provided
    if (profile.funding_types_of_interest.includes('other') && !profile.funding_types_other_text.trim()) {
      setError('Please specify the funding type in the "Other" field');
      setSaving(false);
      return;
    }

    try {
      // Get or create profile
      const response = await api.get('/profile/');

      if (response.data.length > 0) {
        // Update existing profile
        await api.put(`/profile/${response.data[0].id}/`, profile);
      } else {
        // Create new profile
        await api.post('/profile/', profile);
      }

      posthog.capture('profile_completed', {
        roles: profile.roles,
        country_count: profile.country.length,
        county_count: profile.counties.length,
        funding_type_count: profile.funding_types_of_interest.length,
      });

      // Navigate to intended destination or dashboard
      if (redirectIntent?.path) {
        navigate(redirectIntent.path);
      } else {
        navigate('/funding');
      }
    } catch (err) {
      setError('Failed to save profile. Please try again.');
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">
              Complete Your Profile
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please tell us about you as this ensures we have the most relevant opportunities
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What best describes you? <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">Select at least one</p>
              <div className="space-y-2 border border-gray-300 rounded-md p-3">
                {ROLE_CHOICES.map((role) => (
                  <label key={role.value} className="flex items-center">
                    <input
                      type="checkbox"
                      value={role.value}
                      checked={profile.roles.includes(role.value)}
                      onChange={() => handleCheckboxChange('roles', role.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{role.label}</span>
                  </label>
                ))}
              </div>

              {profile.roles.includes('other') && (
                <div className="mt-3">
                  <label htmlFor="role_other_text" className="block text-sm font-medium text-gray-700 mb-1">
                    Please specify <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="role_other_text"
                    name="role_other_text"
                    value={profile.role_other_text}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Describe your role"
                    required={profile.roles.includes('other')}
                  />
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Location
                </label>
                {!isEditingLocation && profile.country.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsEditingLocation(true)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </button>
                )}
              </div>

              {isEditingLocation ? (
                <>
                  <p className="text-xs text-gray-500 mb-3">Select countries</p>
                  <div className="flex flex-wrap gap-4 border border-gray-300 rounded-md p-3 mb-4">
                    {COUNTRY_CHOICES.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={profile.country.includes(option.value)}
                          onChange={() => handleCountryToggle(option.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {profile.country.map(countryValue => {
                    const countryEntry = COUNTIES_BY_COUNTRY.find(c => c.country === countryValue);
                    if (!countryEntry) return null;
                    return (
                      <div key={countryValue} className="mb-4">
                        <p className="text-sm font-medium text-gray-600 mb-2">{countryEntry.country} — Counties</p>
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                          {countryEntry.counties.map((county) => (
                            <label key={county} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={profile.counties.includes(county)}
                                onChange={() => handleCheckboxChange('counties', county)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">{county}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {profile.country.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setIsEditingLocation(false)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Done
                    </button>
                  )}
                </>
              ) : (
                profile.country.length > 0 ? (
                  <div>
                    <div className="flex flex-wrap gap-2">
                      {profile.country.map(c => (
                        <span key={c} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {c}
                        </span>
                      ))}
                    </div>
                    {profile.counties.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {profile.counties.map(c => (
                          <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No location set</p>
                )
              )}
            </div>

            {/* Funding Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What types of funding are you interested in? (Optional)
              </label>
              <p className="text-xs text-gray-500 mb-3">Select all that apply</p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                {FUNDING_TYPE_CHOICES.map((type) => (
                  <label key={type.value} className="flex items-center">
                    <input
                      type="checkbox"
                      value={type.value}
                      checked={profile.funding_types_of_interest.includes(type.value)}
                      onChange={() => handleCheckboxChange('funding_types_of_interest', type.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                  </label>
                ))}
              </div>

              {profile.funding_types_of_interest.includes('other') && (
                <div className="mt-3">
                  <label htmlFor="funding_types_other_text" className="block text-sm font-medium text-gray-700 mb-1">
                    Please specify <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="funding_types_other_text"
                    name="funding_types_other_text"
                    value={profile.funding_types_other_text}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Describe the funding type"
                    required={profile.funding_types_of_interest.includes('other')}
                  />
                </div>
              )}
            </div>


            {/* Submit Button - No skip button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Complete Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSignUpForm;
